import { useState } from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import { useAddPayee, useAddSource } from "../../api/lists";
import { errorText } from "../../api/common";
import {
  SENDER_LABEL,
  type InvoiceSenderRef,
  type SenderKind,
} from "../../logic/invoice-sender";

export type MissingSenderAlertProps = {
  /** Which registry the detected name belongs in. */
  kind: SenderKind;
  /** The sender name exactly as the detector read it. */
  name: string;
  /** Called with the newly created entity once "Add" succeeds. */
  onAdded: (sender: InvoiceSenderRef) => void;
};

/**
 * Shown in the auto-fill review when the detected sender has no entry in its
 * registry yet. Offers to create it under exactly the detected name — one click
 * instead of opening the picker and retyping the vendor — next to the existing
 * ways out (pick another, or drop the row).
 */
export const MissingSenderAlert = ({
  kind,
  name,
  onAdded,
}: MissingSenderAlertProps) => {
  const addPayee = useAddPayee();
  const addSource = useAddSource();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = SENDER_LABEL[kind];
  const noun = label.toLowerCase();

  const handleAdd = async () => {
    setAdding(true);
    setError(null);
    // `style: "normal"` as the registry form stores it, so an entry added
    // here is indistinguishable from one added on its admin page.
    const entity = {
      name,
      description: null,
      colorBg: null,
      colorFg: null,
      style: "normal" as const,
    };
    try {
      if (kind === "payee") {
        const created = await addPayee.mutateAsync(entity);
        onAdded({ kind: "payee", id: created.id });
      } else {
        const created = await addSource.mutateAsync(entity);
        onAdded({ kind: "source", id: created.id });
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Alert
      severity={error ? "error" : "warning"}
      sx={{ mt: 2 }}
      action={
        <Button
          color="inherit"
          size="small"
          disabled={adding}
          startIcon={adding ? <CircularProgress size={14} /> : undefined}
          onClick={() => void handleAdd()}
          sx={{ whiteSpace: "nowrap" }}
        >
          Add {noun}
        </Button>
      }
    >
      {label} “{name}” isn’t in your list yet. Add it, pick an existing {noun}{" "}
      above, or uncheck the {label} row, before you can apply.
      {error && ` Adding it failed: ${error}`}
    </Alert>
  );
};
